const MEDIA_POLL_MS = 500;

// Module level because objects start loading long before the room UI mounts: a React-owned tracker would
// miss every object loaded behind the loading screen.
const listeners = new Set();
let progress = { loadedCount: 0, total: 0, percent: 0, done: false };

function isMediaSettled(mediaEl) {
  // A failed element will never load anything more.
  if (mediaEl.error) return true;

  // MediaSource backed playback (HLS through hls.js, webcam streams) keeps fetching segments for as long
  // as it plays, so it never leaves NETWORK_LOADING: it is settled as soon as it has frames to show.
  if (mediaEl.srcObject || (mediaEl.src || "").startsWith("blob:")) {
    return mediaEl.readyState >= mediaEl.HAVE_CURRENT_DATA;
  }

  // Resource selection is asynchronous, so a freshly sourced element still reports no source: wait for
  // metadata first, then for NETWORK_LOADING to clear, which means the browser stopped pulling bytes
  // either because the file is buffered or because a paused element suspended its download.
  return mediaEl.readyState >= mediaEl.HAVE_METADATA && mediaEl.networkState !== mediaEl.NETWORK_LOADING;
}

// Pinned objects are a tiny objects.gltf that spawns one media entity per object, so byte progress on the
// glTF says nothing and per-media bytes are not observable across browsers: Firefox loads images through
// <img> and audio/video goes through HTMLMediaElement, and the files are cross-origin without
// Timing-Allow-Origin so resource timing reports zero. Progress is therefore the count of loaded entities.
export function trackPinnedObjectLoading(objectsScene) {
  const loadedEntities = new Set();

  const update = () => {
    const loadedCount = loadedEntities.size;
    const total = objectsScene.querySelectorAll("[media-loader]").length;
    // While an object is still loading the denominator is padded, so the readout cannot claim 100% before
    // the last file has settled.
    const pending = total - loadedCount;
    const percent = total ? Math.floor((loadedCount / (total + (pending ? 1 : 0))) * 100) : 0;

    progress = { loadedCount, total, percent, done: total > 0 && pending === 0 };
    listeners.forEach(listener => listener(progress));
  };

  const markSettled = target => {
    loadedEntities.add(target);
    update();
  };

  objectsScene.addEventListener("media-loaded", ({ target }) => {
    // media-video resolves audio as soon as the src is set and video as soon as a frame is decodable, so
    // the file keeps downloading afterwards. Counting those on the event makes the last object read 100%
    // while megabytes are still in flight, so they are counted once their element stops fetching.
    const mediaEl = target.components && target.components["media-video"] && target.components["media-video"].video;

    if (mediaEl && !isMediaSettled(mediaEl)) {
      const poll = setInterval(() => {
        if (!isMediaSettled(mediaEl)) return;
        clearInterval(poll);
        markSettled(target);
      }, MEDIA_POLL_MS);
      return;
    }

    markSettled(target);
  });

  // An object whose media failed never reports itself as loaded, and would hold the readout back forever.
  objectsScene.addEventListener("media-error", ({ target }) => markSettled(target));
}

export function getPinnedObjectLoading() {
  return progress;
}

export function subscribeToPinnedObjectLoading(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
